import { message, Tooltip } from 'antd';
import { first, filter, isEmpty } from 'lodash';
import moment from 'moment';
import { ReactNode } from 'react';
import { parse, format, subDays, startOfDay, endOfDay, parseISO } from 'date-fns';
import {
  COMMON_ERROR, CONSULTATION_TYPES, EMAIL_REGX, MESSAGE_TIMEOUT, MIN_LENGTH_OF_PASSWORD, NOTREQUIRED,
  ONLY_ALPHA_NUMERIC, ONLY_ALPHABET, PASSWORD_MIN_LENGTH_MESSAGE,
  PASSWORD_REQUIRED_MESSAGE, PASSWORD_VALIDATION, MAIL_CONFIGURATION_CONSTANTS,
  GROUPED_ROLES,
  MOBILENO_REPEAT_SEQUENCE_REGEX,
  MOBILE_MASK_REGEX,
  EMAIL_MAX_LENGTH_ERROR,
  HIS_COMMON_DATE_FORMAT,
  UPC_TIME_FORMAT,
  DATE_FILTER,
  PATIENT_SIGNATURE_CONSTANTS,
  CONFIGURATION_VALIDATIONS,
  AUTOMATION_SIMULATION_HOSPITAL,
  DR_AGARWAL_CONST,
} from '../shared/constants/commonConstants';
import React from 'react';
import { Rule } from 'rc-field-form/lib/interface';
import CustomMessageNotification from '../shared/components/customMessageNotification/CustomMessageNotification';
import ApplicationDetail from '../shared/services/applicationDetail.service';
import { containsSequentialCharacters, customGTMEvent, findKeyValuePair, getLookUpFullDetail, getPayerLookUpDetail } from '../modules/crm/inPatient/service/crmUtils';
import { getDateWithTime, getTime } from './dateTimeUtils';
import {decryptText} from './encryptDecrypt';
import { PURPOSE_OF_REQUEST } from '../modules/abdmHIU/abdmConstants';
import zxcvbn from 'zxcvbn';

export function updateObject<I, P>(oldObject: I, updatedProperties: P): I {
  return {
    ...oldObject,
    ...updatedProperties,
  };
}

export function updateAction<T>(type: string, payload?: T): { type: string, payload?: T } {
  return { type, payload };
}

export const getTimeFromSecondsOrMinutes = (seconds: number, type: moment.DurationInputArg2): string => {
  return moment.utc(moment.duration(seconds, type).asMilliseconds()).format('h:mm a');
};

export const getMinutesFromDate = (date: Date): any => {
  return moment.duration(date.getHours() + ':' + date.getMinutes()).asMinutes();
};

export const propertyBasedSort = (a: any, b: any): number => {
  const aDate: Date = new Date(a.start);
  const bDate: Date = new Date(b.start);
  if (aDate < bDate) {
    return -1;
  }
  if (aDate > bDate) {
    return 1;
  }
  return 0;
};

export const success = (msg: ReactNode | string): void => {
  message.success(msg);
};
export const error = (msg: ReactNode | string): void => {
  message.error(msg);
};
export const centeredNotification = (msg: string, type: string = 'error', CustomComponent?: any, duration?: number): void => {
  message.destroy(msg);
  message[type]({
    content: CustomComponent ? <CustomComponent /> : <CustomMessageNotification message={msg || COMMON_ERROR} />,
    className: 'custom-message-width',
    duration: duration || MESSAGE_TIMEOUT,
    key: msg,
  });
};
export const warning = (msg: ReactNode | string): void => {
  message.warning(msg);
};

export const getDate = (date: string): string => {
  return moment(date).format('DD-MM-YYYY');
};

export const capitalizeFirstLetter = (str: string): string => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
};

export const capitalizeFirstLetterAndRestSmallLatter = (str: string): string => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLocaleLowerCase();
};

export const nameFormat = (name: string): string => {
  return name
    ?.split(' ')
    ?.map((part: string): string => {
      if (part?.length === 1) {
        // Preserve single characters (like initials) in uppercase
        return part?.toUpperCase();
      } else {
        // Capitalize first letter and make the rest lowercase for other parts
        return capitalizeFirstLetterAndRestSmallLatter(part);
      }
    })
    ?.join(' ');
};

export const base64Encode = (unencoded: string): string => {
  return btoa(unencoded);
};

export const base64Decode = (encoded: string): string => {
  return atob(encoded);
};

export const base64DecodeForGrievance = (encoded: string): string => { // URL decode response for Grievance
  return decodeURIComponent(atob(encoded));
};

export const base64UrlEncode = (unencoded: string | null): string => {
  const encoded = base64Encode(unencoded as string);
  return encodeURIComponent(encoded);
};

export const base64UrlDecode = (encoded: string): string => {
  encoded = decodeURIComponent(encoded);
  return base64Decode(encoded);
};

export const base64UrlDecodeForGrievance = (encoded: string): string => { // URL decode function for Grievance
  encoded = decodeURIComponent(encoded);
  return base64DecodeForGrievance(encoded);
};

export const base64UrlEncoded = (unencoded: string): string => {
  const encoded = base64Encode(encodeURIComponent(unencoded));
  return encodeURIComponent(encoded);
};

export const base64UrlDecoded = (encoded: string): string => {
  encoded = base64Decode(decodeURIComponent(encoded));
  return decodeURIComponent(encoded);
};

export const extractSecondLevelBaseUrl = (path: string): string => {
  const routePath = path.split('/');
  routePath.length = 3;
  return routePath.join('/');
};

export const getDobFromAge = (age: number): Date => {
  const today = new Date();
  const birthYear = today?.getFullYear() - age;
  const dob = new Date(birthYear, today?.getMonth(), today?.getDate());
  return dob;
};

export const getAgeFromDob = (dob: Date): number => {
  return dob ? moment().diff(dob, 'years') : 0;
};

export const array_move = (arr: any[], old_index: number, new_index: number): any => {
  if (new_index >= arr.length) {
    let k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
};

export const CalculateFinalPrice = (price: number, discountPercent: number): number => {
  discountPercent = discountPercent < 0 || discountPercent === undefined ? 0 : discountPercent > 100 ? 100 : discountPercent;
  price = price === undefined ? 0 : price;
  return Math.round((price - ((discountPercent / 100) * price)) || 0);
};

export const CalculateDiscountPercentage = (price: number, discountedPrice: number): number => {
  price = price < 0 || price === undefined ? 0 : price;
  discountedPrice = discountedPrice === undefined ? 0 : discountedPrice;
  return Math.round(((price - discountedPrice) / price) * 100 || 0);
};

export const GetFirstElement = (array: any[]): any => {
  return first(array) as any;
};
export const getMobileFormat = (mobile: string): string => {
  if (!mobile || typeof mobile !== 'string' || mobile.length !== 10) {
    return '';
  }
  // Check against regex (blocks invalid numbers like all identical digits)
  const formattedMobile = `+91 ${mobile.slice(0, 3)} ${mobile.slice(3, 7)} ${mobile.slice(7)}`;
  if (!MOBILENO_REPEAT_SEQUENCE_REGEX.test(formattedMobile)) {
    return ''; // Invalid number, do not prefill
  }
  return formattedMobile;
};

export const formatName = (name: string): string => { // to remove titles in the name
  if (!name) {
    return '';
  }

  const prefixes = ['Mr.', 'Mr', 'Ms.', 'Ms', 'Mrs.', 'Mrs'];
  const [title, ...restName] = name?.split(' ');

  return prefixes?.includes(title) ? restName?.join(' ') : name;
};

export const formatPrice = (price: number, isDecimalRequired: boolean = true, isRSRequried: boolean = true): string => {
  if (isDecimalRequired && isRSRequried) {
    return '₹ ' + (price || 0).toFixed(2).replace(/(\d)(?=(\d{2})+\d\.)/g, '$1,');
  } else if (isDecimalRequired && !isRSRequried) {
    return (price || 0).toFixed(2).replace(/(\d)(?=(\d{2})+\d\.)/g, '$1,');
  } else if (!isDecimalRequired && isRSRequried) {
    return '₹ ' + (price || 0).toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ',');
  } else if (!isDecimalRequired && !isRSRequried) {
    return (price || 0).toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ',');
  } else {
    return '₹ ' + (price || 0).toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ',');
  }
};

export const getPercentage = (total: number, value: number): number => {
  return (total && value) ? Math.round((value / total) * 100) : 0;
};

export const mobileFormat = (mobileNumber: string): string => {
  return mobileNumber.substr(4).split(' ').join('');
};

export const toCamelCase = (string: string): string => {
  return string
    .replace(/\s(.)/g, ($1: any): any => $1.toUpperCase())
    .replace(/\s/g, ' ')
    .replace(/^(.)/, ($1: any): any => $1.toUpperCase());
};

export const checkIsSSo = (): boolean => {
  const paths = window?.location?.pathname?.split('/');
  return (paths && paths.length > 0 && paths[1] === 'sl');
};

export const checkIsDeeplink = (): boolean => {
  const urlParams = new URLSearchParams(window?.location?.search);
  const origin = urlParams.get('origin');
  return origin === DR_AGARWAL_CONST.ORIGIN;
};

export const average = (arr: number[]): number => arr.reduce((p: number, c: number): number => p + c, 0) / arr.length;

export const getConsultationType = (consultationType: string): string | undefined => {
  return CONSULTATION_TYPES?.find((v: any): any => v?.name === consultationType)?.value || '';
};
export const getBulletPoints = (values: string[]): string => {
  if (values) {
    let result = `<ul style="width: 100%; padding-inline-start: 15px; margin-bottom: 0;">`;
    values.forEach((x: string): void => {
      result += '<li>' + x + '</li>';
    });
    result = result + '</ul>';
    return result;
  }
  return '';
};

export const emptyFunction = (): void => { };

export const convertToValidStr = (str: string = ''): string => {
  return str
    .replace(/^[\s]+/gm, '')
    .replace(/[\s]+$/gm, ' ');
};

export const handleConfirmation = (
  messageText: string, callback: any, routeGuardRef: React.MutableRefObject<any> = { current: {} }): any => {
  const { current: { getConfirmation } } = routeGuardRef;
  if (getConfirmation) {
    getConfirmation(messageText, callback);
  }
};

export const getPasswordValidationRules = (key: string = 'newPassword', userName: string): Rule[] => {
  const rules: Rule[] = [
    { required: true, message: PASSWORD_REQUIRED_MESSAGE },
  ];
  PASSWORD_VALIDATION?.forEach(({ regx, errorMessage }: any): any => {
    const validationFn = ({ getFieldValue }: any): any => ({
      validator(): any {
        if (getFieldValue(key)) {
          if (!(getFieldValue(key)?.match(regx))) {
            return Promise.reject(errorMessage);
          } else {
            return Promise.resolve();
          }
        } else {
          return Promise.resolve();
        }
      },
    });
    rules.push(validationFn);
  });
  const sequentialCharacterValidator = ({ getFieldValue }: any): any => ({
    validator(): any {
      if (containsSequentialCharacters(getFieldValue(key))) {
        return Promise.reject('- Password should not contain any 3 sequential numbers or letters.');
      } else {
        return Promise.resolve();
      }
    },
  });
  rules.push(sequentialCharacterValidator);
  const lengthValidator = ({ getFieldValue }: any): any => ({
    validator(): any {
      if (getFieldValue(key)) {
        if (getFieldValue(key)?.length < MIN_LENGTH_OF_PASSWORD) {
          return Promise.reject(PASSWORD_MIN_LENGTH_MESSAGE);
        } else if ((userName && getFieldValue(key)?.toLowerCase()?.includes(userName?.split('@')?.[0]?.toLowerCase()))) {
          return Promise.reject('- Password should not contain the username.');
        } else {
          return Promise.resolve();
        }
      } else {
        return Promise.resolve();
      }
    },
  });
  rules.push(lengthValidator);
  const commonWordsValidator = ({ getFieldValue }: any): any => ({
    validator(): any {
      const value = getFieldValue(key);
      if (value) {
        const result = zxcvbn(value);
        if (result?.score < 3) { // Score ranges from 0 (weakest) to 4 (strongest)
          return Promise.reject(`- Password is too weak: ${result.feedback.suggestions.join(', ')}`);
        }
      }
      return Promise.resolve();
    },
  });
  rules.push(commonWordsValidator);
  return rules;
};

export const isValidImage = (imageUrl: string): boolean => {
  return (imageUrl !== '' && imageUrl !== null && imageUrl !== undefined) ? true : false;
};

export const replaceRsAndComma = (value: string): string =>
  value.length > 0 ? value.toString().replace(/[₹, ]/g, '') : '';

export const removeCurrencySymbols = (value: string): string => {
  if (!value) {
    return '';
   }
  const cleanedValue = value?.toString()?.replace(/[₹, ]/g, ''); // Remove ₹, commas, and spaces
  return cleanedValue;
};

export const replaceUnderscoreAndDot = (value: string): string => value.length > 0 ? value.toString().replace(/[_. ]/g, '') : '';

export const getUniqueMeetId = (providerId: string, userId: string, payerUniqueId: string): string => {
  return `${providerId}-${userId}-${Date.now()}-${payerUniqueId}`;
};

export const getEmailConfigFormElements = (result: any): any => {
  return {
    hospitalMailID: result?.hospitalMailID || undefined,
    hospitalMailPassword: result?.hospitalMailPassword || undefined,
    hospitalSecondaryMailIds: result?.hospitalSecondaryMailIds || [],
    incomingMailServerHostName: result?.incomingMailServerHostName || undefined,
    incomingMailServerPort: result?.incomingMailServerPort || undefined,
    outGoingMailServerHostName: result?.outGoingMailServerHostName || undefined,
    outGoingMailServerPort: result?.outGoingMailServerPort || undefined,
    isEmailVerified: result?.isEmailVerified || false,
    passwordExpire: result?.passwordExpire || false,
    delayPressCount: result?.delayPressCount || null,
    delayDate: result?.delayDate || null,
    enableSSL: result?.enableSSL || true,
    rohiniID: result?.rohiniID || null,
    passwordType: result?.passwordType || '',
  };
};

export const sortByUid = (array: any[]): any[] => {
  return array?.sort((a: any, b: any): number => {
    const aUid = (typeof a?.uid === 'string' ? a?.uid.split('-')?.[3] : '0') || '0';
    const bUid = (typeof b?.uid === 'string' ? b?.uid.split('-')?.[3] : '0') || '0';
    return bUid - aUid;
  });
};

const checkLocalStorageForAccess = (accessCheckKeyword: string): boolean => {
  const userDetail = JSON.parse(decryptText(localStorage.getItem('userDetail') || '{}'));
  const { features = [] } = userDetail;
  const availableFeature = features?.find(({ featureName }: any): boolean => featureName === accessCheckKeyword);
  return !!availableFeature;
};

export const canActivate = (accessCheckKeyword: string): boolean => {
  const appDetail = ApplicationDetail.instance;
  return (
    process.env.REACT_APP_IS_ROLE_BASED_ENABLED === 'false' ||
    accessCheckKeyword === NOTREQUIRED ||
    appDetail.getIfAccessEnabled(accessCheckKeyword) ||
    checkLocalStorageForAccess(accessCheckKeyword));
};

export const isPageNameProfile = (path: string): boolean => {
  return path.includes('profile');
};

export const isValidSearchKey = (searchString: any): boolean => {
  if (isNaN(searchString)) {
    centeredNotification('Please enter correct provider id.');
  }
  return !isNaN(searchString);
};

export const openInNewTabWithAuth = (url: string, token: string): void => {
  const urlWithToken = `${url}?token=${token}`;
  window.open(urlWithToken, '_blank', 'noopener,noreferrer');
};

export const trimEmails = (emails?: string | null): string => {
  return emails?.split(',')
    .map((email: string): string => email.trim())
    .filter((email: string): boolean => !!email)
    .join(',') || '';
};

export const multiEmailValidation = (formItemKey: any,
  compareWithAdditionlData: string[] = [], maxLength: number = 50): any => {
  return ({ getFieldValue }: any): any => ({
    validator(): any {
      const currFieldValue = getFieldValue(formItemKey)
        ?.split(',')?.filter((ele: any): boolean => ele)?.map((em: string): string => em);

      const filteredArray = compareWithAdditionlData?.filter((value: any): any => currFieldValue?.includes(value));
      if (filteredArray?.length > 0) {
        return Promise.reject('Please remove already configured email.');
      }
      if (currFieldValue !== undefined) {
        if (currFieldValue?.length === 0) {
          return Promise.resolve();
        } else if (new Set(currFieldValue)?.size !== currFieldValue?.length) {
          return Promise.reject('Please remove duplicate');
        } else {
          let isInvalidEmail = false;
          for (const iterator of currFieldValue) {
            if (iterator !== iterator?.trim()) {
              return Promise.reject('Please remove leading or trailing white spaces from the emails.');
            }
            if (iterator?.length > maxLength) {
              return Promise.reject(EMAIL_MAX_LENGTH_ERROR(maxLength));
            }
            if (!EMAIL_REGX.test(iterator?.toLowerCase()?.trim())) {
              isInvalidEmail = true;
              break;
            }
          }
          if (isInvalidEmail) {
            return Promise.reject('Please enter valid email.');
          } else {
            return Promise.resolve();
          }
        }
      } else {
        return Promise.resolve();
      }
    },
  });
};

export const countArticle = ({ articles }: any): number => {
  if (articles) {
    const keys = Object.keys(articles);
    if (!keys.length) {
      return 0;
    } else {
      let count = 0;
      for (const iterator of keys) {
        count += articles[iterator]?.length;
      }
      return count;
    }
  }
  return 0;
};

export const getTotalArticleList = (blogs: any): any[] => {
  const list: any[] = [];
  for (const iterator of blogs) {
    if (Object.keys(iterator['articles'])?.length) {
      Object.keys(iterator['articles'])?.forEach((key: string): any => {
        list.push({
          module: iterator,
          subModule: key,
          articles: JSON.stringify(iterator['articles'][key]),
        });
      });
    }
  }
  return list;
};

export const getRule = (type: string, formItemKey: any): Rule => {
  switch (type) {
    case 'payerEmailID':
      return multiEmailValidation(formItemKey);
    default:
      return { required: true, message: '' };
  }
};

export const getTruncatedString = (keyword: string): any => {
  const truncatedString = keyword.length > 10 ? keyword.substring(0, 10) + '...' : keyword;
  return (
    <Tooltip title={keyword === truncatedString ? null : keyword}>
      {truncatedString}
    </Tooltip>
  );
};

export const prepareRoles = (roleList: string[]): any[] => {
  const mappedRoles: any[] = [];
  roleList.map((ele: string): any => {
    const [, typeName, typeId] = ele.split('-');
    if (typeName && typeId) {
      mappedRoles.push({ typeName, typeId: parseInt(typeId, 10) });
    }
    return mappedRoles;
  });
  return mappedRoles;
};

export const getInitialRoleConfig = (roles: any[], { functions }: any): any[] => {
  const fnArr = functions?.map(({ typeId }: any): any => typeId) || [];
  if (!functions) {
    return roles?.map((ele: any): any => {
      ele['disabled'] = true;
      ele['switchText'] = 'Read Only';
      return ele;
    });
  } else {
    return roles?.map((ele: any): any => {
      const { roleID: roleIDR } = findKeyValuePair(ele?.roles || [], 'access', 'R') || {};
      const { roleID: roleIDW } = findKeyValuePair(ele?.roles || [], 'access', 'W') || {};
      ele['disabled'] = true;
      ele['switchText'] = 'Read Only';
      if (roleIDR && fnArr?.includes(roleIDR)) {
        ele['disabled'] = false;
        ele['switchText'] = 'Read Only';
        if (roleIDW && fnArr.includes(roleIDW)) {
          ele['switchText'] = 'Edit Access';
        }
      }
      return ele;
    });
  }
};

const removeRSIcon = (data: string): string =>
  data?.length > 0 ? data?.toString()?.replace(/[₹, ]/g, '') : '0';

export const mapClaimPdfData = (
  formType: string,
  hospitalDetails: any,
  admissionFormVal: any,
  userDetails: any,
  insuranceCompany: string,
  payerRoomType: string,
  payer: any,
  admissionFormType: any,
  lookUp: any,
  payerLookUp: any,
  insurerID?: any,
  mappedAilmentValues?: any,
  hospitalSeal?: any,
  doctorSignature?: any,
  patientSignature?: any,
): any => {
  let data = {};
  const { uniqueIdentifier } = payer || {};
  let totalExpectedCost = 0;
  if (admissionFormType === 'NORMAL') {
    filter(admissionFormVal?.billDetail, (bill: string): any => {
      if (bill && !isNaN(Number(removeRSIcon(bill)))) {
        totalExpectedCost = totalExpectedCost + Number(removeRSIcon(bill));
      }
    });
  } else if (admissionFormType === 'MULTIPAYER') {
    totalExpectedCost = admissionFormVal?.otherFormDetail?.claimedAmount?.toString()?.replace(/\D/g, '') || '0';
  }
  if (formType === 'cashlessForm' && admissionFormType === 'NORMAL') {
    data = {
      formType,
      hospitalID: `${!payer?.isRpaPayer ? payer?.payerHospitalID || '' : ''}`,
      hospitalEmail: hospitalDetails?.providerEmailAddress || '',
      hospitalName: hospitalDetails?.providerName || '',
      hospitalPhoneNo: hospitalDetails?.providerName || '',
      hospitalAddress: hospitalDetails?.providerAddress || '',
      corporateName: payer?.name || admissionFormVal?.customerDetail?.payer || '',
      patientName: admissionFormVal?.customerDetail?.fullName || '',
      patientPhoneNo: admissionFormVal?.customerDetail?.mobile || '',
      patientDOB: admissionFormVal?.customerDetail?.dateOfBirth ? getDate(admissionFormVal?.customerDetail?.dateOfBirth) : '',
      patientAge: (admissionFormVal?.customerDetail?.memberAge)?.toString() || '',
      patientSex: admissionFormVal?.customerDetail?.memberGenderSexRadio || admissionFormVal?.customerDetail?.memberGenderSex ||
        admissionFormVal?.customerDetail?.memberGenderSelect || '',
      MAID: payer?.isRpaPayer ? '' : findKeyValuePair(payerLookUp[uniqueIdentifier]?.insurerName, 'value',
        admissionFormVal?.customerDetail?.insurerName)?.payerMasterValue,
      policyNo: admissionFormVal?.customerDetail?.policyNumber || '',
      employeeID: admissionFormVal?.customerDetail?.employeeId || '',
      insuranceCompany: insuranceCompany || admissionFormVal?.customerDetail?.insurerName || '',
      insuranceCardNumber: admissionFormVal?.customerDetail?.insuranceCardNumber || undefined,
      emailID: admissionFormVal?.customerDetail?.emailId || '',
      icd10: admissionFormVal?.treatmentDetail?.icd10 || '',
      docName: admissionFormVal?.treatmentDetail?.doctorName || '',
      DOA: admissionFormVal?.treatmentDetail?.stayLength?.[0] ? getDate(admissionFormVal?.treatmentDetail?.stayLength?.[0]) : '',
      timeOfAdmission: admissionFormVal?.treatmentDetail?.stayLength?.[0] ? getTime(admissionFormVal?.treatmentDetail?.stayLength?.[0])
        : '',
      DOD: admissionFormVal?.treatmentDetail?.stayLength?.[1] ? getDate(admissionFormVal?.treatmentDetail?.stayLength?.[1]) : '',
      RohiniId: hospitalDetails?.providerRohiniCode || '',
      roomRentAndNursing: (parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Room rent & Nursing charges']), 10)
        + parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Total rent']), 10) + parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Nursing Charge']), 10)).toString(),
      investigation: (parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Investigation']), 10)
        + parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Investigations']), 10)).toString(),
      icuCharges: removeRSIcon(admissionFormVal?.billDetail?.['ICU Charges']),
      otCharges: removeRSIcon(admissionFormVal?.billDetail?.['OT charges']),
      consultation: (parseInt(removeRSIcon(admissionFormVal?.billDetail?.Consultation), 10)
        + parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Surgeon & anesthetist']), 10) +
        parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Physician Charge']), 10) +
        parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Surgeon Charge']), 10) +
        parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Anesthetist']), 10)
      ).toString(),
      medicineAndConsumables: (parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Medicine & consumables']), 10)
        + parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Consumables']), 10) +
        parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Pharmacy']), 10)
      ).toString(),
      otherExpenses: (parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Other hospital expenses']), 10) +
        parseInt(removeRSIcon(admissionFormVal?.billDetail?.['Others']), 10)).toString(),
      patientDiet: removeRSIcon(admissionFormVal?.billDetail?.['Food & Beverages']),
      allInclusivePackageCharges: removeRSIcon(admissionFormVal?.billDetail?.Package),
      totalExpectedCost: `${totalExpectedCost}`,
      hospitalRoomType: isEmpty(payerRoomType) ? admissionFormVal?.treatmentDetail?.hospitalRoomType : payerRoomType,
      proposedLineOfTreatment: payerLookUp[uniqueIdentifier]?.proposedLineOfTreatment?.length > 0 ?
        getPayerLookUpDetail([admissionFormVal?.treatmentDetail?.proposedLineOfTreatment],
          payerLookUp[uniqueIdentifier]?.proposedLineOfTreatment)[0]?.name :
        getLookUpFullDetail([admissionFormVal?.treatmentDetail?.proposedLineOfTreatment], lookUp?.proposedLineOfTreatment, false)[0]?.name,
      automatedProcedure: getLookUpFullDetail([admissionFormVal?.treatmentDetail?.automatedProcedure],
        payerLookUp[payer.id]?.automationProcedures, false)[0].name || '',
      diagnosis: admissionFormVal?.treatmentDetail?.diagnosisDropDown ? getLookUpFullDetail(
        Array(admissionFormVal?.treatmentDetail?.diagnosisDropDown), payerLookUp[payer.id]?.diagnosis, false).map((p: any): any => p.name)
        : getLookUpFullDetail(admissionFormVal?.treatmentDetail?.diagnosis,
          payerLookUp[payer.id]?.diagnosis, false).map((p: any): any => p.name) || [],
      procedure: admissionFormVal?.treatmentDetail?.procedureDropDown ? getLookUpFullDetail( // adding other procedure dropdown
        Array(admissionFormVal?.treatmentDetail?.procedureDropDown), payerLookUp[payer.id]?.procedure, false).map((p: any): any => p.name)
        : getLookUpFullDetail(admissionFormVal?.treatmentDetail?.procedure, payerLookUp[payer.id]?.procedure, false)
          .map((p: any): any => p.name) || '',
      routeOfAdministration: admissionFormVal?.treatmentDetail?.routeOfAdministration || '',
      icd10pcs: admissionFormVal?.treatmentDetail?.icd10pcs || '',
      eye: admissionFormVal?.treatmentDetail?.eye || '',
      treatmentApproach: admissionFormVal?.treatmentDetail?.treatmentApproach || '',
      dialysis: admissionFormVal?.treatmentDetail?.dialysis || '',
      position: admissionFormVal?.treatmentDetail?.position || [],
      obstetricHistoryA: admissionFormVal?.treatmentDetail?.obstetricHistoryA || '',
      obstetricHistoryG: admissionFormVal?.treatmentDetail?.obstetricHistoryG || '',
      obstetricHistoryL: admissionFormVal?.treatmentDetail?.obstetricHistoryL || '',
      obstetricHistoryP: admissionFormVal?.treatmentDetail?.obstetricHistoryP || '',
      deliveryDate: admissionFormVal?.treatmentDetail?.deliveryDate ? getDate(admissionFormVal?.treatmentDetail?.deliveryDate) : '',
      maternityType: admissionFormVal?.treatmentDetail?.maternityType || '',
      admissionType: admissionFormVal?.treatmentDetail?.admissionType || '',
      rpaAdmissionType: admissionFormVal?.treatmentDetail?.rpaAdmissionType ?
        admissionFormVal?.treatmentDetail?.rpaAdmissionType.toString() : '' || '',
      isDischargeCase: admissionFormVal?.treatmentDetail?.isDischargeCase ?
        admissionFormVal?.treatmentDetail?.isDischargeCase[0] === 'Yes' ? 'true' : 'false' : 'false' || '',
      isDeathCase: admissionFormVal?.treatmentDetail?.isDeathCase ? admissionFormVal?.treatmentDetail?.isDeathCase[0] === 'Yes' ? 'true' : 'false' : 'false' || '',
      isCovidCase: admissionFormVal?.treatmentDetail?.isCovidCase ? admissionFormVal?.treatmentDetail?.isCovidCase[0] === 'Yes' ? 'true' : 'false' : 'false' || '',
      isMultipleSurgeries: admissionFormVal?.treatmentDetail?.isMultipleSurgeries ?
        admissionFormVal?.treatmentDetail?.isMultipleSurgeries.toString() : '' || '',
      ailment: getLookUpFullDetail(admissionFormVal?.treatmentDetail?.ailment, lookUp.pastHistory, false).map((p: any): any => p.name),
      otherAilment: admissionFormVal?.treatmentDetail?.otherAilment || '',
      dateOfInjury: admissionFormVal?.treatmentDetail?.dateOfInjury ? getDate(admissionFormVal?.treatmentDetail?.dateOfInjury) : '',
      firNumber: admissionFormVal?.treatmentDetail?.firNumber || '',
      isItRTA: admissionFormVal?.treatmentDetail?.isItARta ? admissionFormVal?.treatmentDetail?.isItARta.toString() : '' || '',
      injuryOrDeseaseCaused: admissionFormVal?.treatmentDetail?.injuryOrDeseaseCaused ?
        admissionFormVal?.treatmentDetail?.injuryOrDeseaseCaused.toString() : '' || '',
      isReportedToPolice: admissionFormVal?.treatmentDetail?.isReportedToPolice ?
        admissionFormVal?.treatmentDetail?.isReportedToPolice.toString() : '' || '',
      testConductedToEstablishedThis: admissionFormVal?.treatmentDetail?.testConductedToEstablishedThis ?
        admissionFormVal?.treatmentDetail?.testConductedToEstablishedThis.toString() : '' || '',
      docRegNo: admissionFormVal?.treatmentDetail?.doctorRegNo || '',
      highRiskCharges: removeRSIcon(admissionFormVal?.billDetail?.['High Risk Charges']),
      ambulanceCharges: removeRSIcon(admissionFormVal?.billDetail?.['Ambulance Charges']),
      bloodCharges: removeRSIcon(admissionFormVal?.billDetail?.['Blood Charges']),
      cathlabCharges: removeRSIcon(admissionFormVal?.billDetail?.['Cathlab Charges']),
      implantCharges: removeRSIcon(admissionFormVal?.billDetail?.['Implants Charges']),
      professionalCharges: removeRSIcon(admissionFormVal?.billDetail?.['Professional Fees']),
      diabeticCharges: removeRSIcon(admissionFormVal?.billDetail?.['Diabetic Charges']),
      medicalEquipmentCharges: removeRSIcon(admissionFormVal?.billDetail?.['Medical Equipment']),
      shortName: payer?.shortName || '',
      qualification: admissionFormVal?.treatmentDetail?.doctorQualificationDropDown ?
        admissionFormVal?.treatmentDetail?.doctorQualificationDropDown : admissionFormVal?.treatmentDetail?.doctorQualification || '',
      dateOfDiagnosis: admissionFormVal?.treatmentDetail?.dateOfDiagnosis ?
        getDate(admissionFormVal?.treatmentDetail?.dateOfDiagnosis) : '',
      attendantContactNumber: admissionFormVal?.customerDetail?.attendantContactNumber,
      doctorMobileNo: admissionFormVal?.treatmentDetail?.doctorMobileNo,
      payerId: payer?.id,
      insurerId: insurerID,
      panNo: admissionFormVal?.customerDetail?.kycPan,
      aadharNo: admissionFormVal?.customerDetail?.kycAdhaar,
      memberId: admissionFormVal?.customerDetail?.memberId || '',
      mandatoryPastHistory: mappedAilmentValues || '',
      policyHolderName: admissionFormVal?.customerDetail?.corporateName || '',
      hospitalSeal,
      doctorSign: doctorSignature,
      patientSign: patientSignature,
    };
  } else
  if (formType === 'cashlessForm' && admissionFormType === 'MULTIPAYER') {
    data = {
      formType,
      hospitalID : '',
      hospitalEmail : hospitalDetails?.providerEmailAddress || '',
      hospitalName : hospitalDetails?.providerName || '',
      hospitalPhoneNo : hospitalDetails?.providerName || '',
      hospitalAddress : hospitalDetails?.providerAddress || '',
      corporateName : payer?.name || '',
      docRegNo: admissionFormVal?.otherFormDetail?.doctorRegNo || '',
      patientName : admissionFormVal?.otherFormDetail?.fullName,
      patientPhoneNo : admissionFormVal?.otherFormDetail?.mobile || '',
      patientDOB : '',
      patientAge : admissionFormVal?.otherFormDetail?.memberAge,
      MAID : admissionFormVal?.otherFormDetail?.insurerName,
      policyNo : admissionFormVal?.otherFormDetail?.policyNumber || '',
      employeeID : admissionFormVal?.otherFormDetail?.employeeId || '',
      insuranceCompany: insuranceCompany || admissionFormVal?.customerDetail?.insurerName || '',
      emailID: admissionFormVal?.otherFormDetail?.emailId || '',
      icd10 : admissionFormVal?.otherFormDetail?.icd10 || '',
      docName : admissionFormVal?.otherFormDetail?.doctorName || '',
      DOA : admissionFormVal?.otherFormDetail?.stayLength?.[0] ? getDate(admissionFormVal?.otherFormDetail?.stayLength?.[0]) : '',
      timeOfAdmission: admissionFormVal?.otherFormDetail?.staylength?.[0] ?
        getTime(admissionFormVal?.otherFormDetail?.stayLength?.[0]) : '',
      DOD : admissionFormVal?.otherFormDetail?.stayLength?.[1] ? getDate(admissionFormVal?.otherFormDetail?.stayLength?.[1]) : '',
      RohiniId : hospitalDetails?.providerRohiniCode || '',
      roomRentAndNursing : removeRSIcon(admissionFormVal?.otherFormDetail?.['Room rent & Nursing charges']),
      investigation : removeRSIcon(admissionFormVal?.otherFormDetail?.Investigations),
      icuCharges : removeRSIcon(admissionFormVal?.otherFormDetail?.['ICU Charges']),
      otCharges : removeRSIcon(admissionFormVal?.otherFormDetail?.['OT charges']),
      consultation: (
        parseInt(removeRSIcon(admissionFormVal?.otherFormDetail?.Consultation || '0'), 10) +
        parseInt(removeRSIcon(admissionFormVal?.otherFormDetail?.['Surgeon & anesthetist'] || '0'), 10)
      ).toString(),
      medicineAndConsumables : removeRSIcon(admissionFormVal?.otherFormDetail?.['Medicine & consumables']),
      otherExpenses : removeRSIcon(admissionFormVal?.otherFormDetail?.['Other hospital expenses']),
      allInclusivePackageCharges: removeRSIcon(admissionFormVal?.otherFormDetail?.Package),
      totalExpectedCost: `${totalExpectedCost}`,
      hospitalRoomType: getPayerLookUpDetail([admissionFormVal?.otherFormDetail?.payerRoomType],
        payerLookUp[uniqueIdentifier]?.roomType)[0]?.name || '',
      proposedLineOfTreatment: payerLookUp[uniqueIdentifier]?.proposedLineOfTreatment?.length > 0 ?
        getPayerLookUpDetail([admissionFormVal?.otherFormDetail?.proposedLineOfTreatment],
          payerLookUp[uniqueIdentifier]?.proposedLineOfTreatment)[0]?.name :
        getLookUpFullDetail([admissionFormVal?.otherFormDetail?.proposedLineOfTreatment], lookUp?.proposedLineOfTreatment, false)[0]?.name,
      automatedProcedure: getLookUpFullDetail([admissionFormVal?.otherFormDetail?.automatedProcedure],
        payerLookUp[payer.id]?.automationProcedures, false)[0].name || '',
      diagnosis: getLookUpFullDetail(admissionFormVal?.otherFormDetail?.diagnosis,
        payerLookUp[payer.id]?.diagnosis, false).map((p: any): any => p.name) || [],
      procedure: admissionFormVal?.otherFormDetail?.procedure || [],
      routeOfAdministration: admissionFormVal?.otherFormDetail?.routeOfAdministration || '',
      icd10pcs: admissionFormVal?.otherFormDetail?.icd10pcs || '',
      eye: admissionFormVal?.otherFormDetail?.eye || '',
      treatmentApproach: admissionFormVal?.otherFormDetail?.treatmentApproach || '',
      dialysis: admissionFormVal?.otherFormDetail?.dialysis || '',
      position: admissionFormVal?.otherFormDetail?.position || [],
      obstetricHistoryA: admissionFormVal?.otherFormDetail?.obstetricHistoryA || '',
      obstetricHistoryG: admissionFormVal?.otherFormDetail?.obstetricHistoryG || '',
      obstetricHistoryL: admissionFormVal?.otherFormDetail?.obstetricHistoryL || '',
      obstetricHistoryP: admissionFormVal?.otherFormDetail?.obstetricHistoryP || '',
      maternityType: admissionFormVal?.otherFormDetail?.maternityType || '',
      admissionType: admissionFormVal?.otherFormDetail?.admissionType || '',
      rpaAdmissionType: admissionFormVal?.otherFormDetail?.rpaAdmissionType ?
        admissionFormVal?.treatmentDetail?.otherFormDetail.toString() : '' || '',
      isDischargeCase: admissionFormVal?.otherFormDetail?.isDischargeCase ?
        admissionFormVal?.otherFormDetail?.isDischargeCase[0] === 'Yes' ? 'true' : 'false' : 'false' || '',
      isDeathCase: admissionFormVal?.otherFormDetail?.isDeathCase ? admissionFormVal?.otherFormDetail?.isDeathCase[0] === 'Yes' ? 'true' : 'false' : 'false' || '',
      isCovidCase: admissionFormVal?.otherFormDetail?.isCovidCase ? admissionFormVal?.otherFormDetail?.isCovidCase[0] === 'Yes' ? 'true' : 'false' : 'false' || '',
      isMultipleSurgeries: admissionFormVal?.otherFormDetail?.isMultipleSurgeries ?
        admissionFormVal?.otherFormDetail?.isMultipleSurgeries.toString() : '' || '',
      ailment: getLookUpFullDetail(admissionFormVal?.otherFormDetail?.ailment, lookUp.pastHistory, false).map((p: any): any => p.name),
      otherAilment: admissionFormVal?.otherFormDetail?.otherAilment || '',
      firNumber: admissionFormVal?.otherFormDetail?.firNumber || '',
      isItRTA: admissionFormVal?.otherFormDetail?.isItARta ? admissionFormVal?.otherFormDetail?.isItARta.toString() : '' || '',
      injuryOrDeseaseCaused: admissionFormVal?.otherFormDetail?.injuryOrDeseaseCaused ?
        admissionFormVal?.otherFormDetail?.injuryOrDeseaseCaused.toString() : '' || '',
      isReportedToPolice: admissionFormVal?.otherFormDetail?.isReportedToPolice ?
        admissionFormVal?.otherFormDetail?.isReportedToPolice.toString() : '' || '',
      testConductedToEstablishedThis: admissionFormVal?.otherFormDetail?.testConductedToEstablishedThis ?
        admissionFormVal?.otherFormDetail?.testConductedToEstablishedThis.toString() : '' || '',
      shortName: payer?.shortName || '',
      payerId: payer?.id,
      insurerId: insurerID,
      memberId : admissionFormVal?.otherFormDetail?.memberId || '',
      policyHolderName: admissionFormVal?.otherFormDetail?.corporateName || '',
      hospitalSeal,
      doctorSign: doctorSignature,
      patientSign: patientSignature,
    };
  } else if (formType === 'acknowledgementLetter' && admissionFormType === 'NORMAL') {
    data = {
      formType,
      patientName : admissionFormVal?.customerDetail?.fullName || '',
      patientPhoneNo : admissionFormVal?.customerDetail?.mobile || '',
      admissionType : '',
      hospitalName : hospitalDetails?.providerName || '',
      hospitalAddress : hospitalDetails?.providerAddress || '',
      hospitalNo : `${hospitalDetails?.providerId}` || '',
      ipNo : admissionFormVal?.customerDetail?.inPatientNumber || '',
      hospitalisation : '',
      memberId : admissionFormVal?.customerDetail?.memberId || '',
      policyHolder : admissionFormVal?.customerDetail?.fullName || '',
      cashlessClaimDate : new Date().toLocaleString().replace(',', ''),
      ihxUserID : userDetails?.userName || '',
      shortName: payer?.shortName || '',
    };
  } else if (formType === 'acknowledgementLetter' && admissionFormType === 'MULTIPAYER') {
    data = {
      formType,
      patientName : admissionFormVal?.otherFormDetail?.fullName || '',
      patientPhoneNo : admissionFormVal?.otherFormDetail?.mobile || '',
      admissionType : '',
      hospitalName : hospitalDetails?.providerName || '',
      hospitalAddress : hospitalDetails?.providerAddress || '',
      hospitalNo : `${hospitalDetails?.providerId}` || '',
      ipNo : admissionFormVal?.otherFormDetail?.inPatientNumber || '',
      hospitalisation : '',
      memberId : admissionFormVal?.otherFormDetail?.memberId || '',
      policyHolder : admissionFormVal?.otherFormDetail?.fullName || '',
      cashlessClaimDate : new Date().toLocaleString().replace(',', ''),
      ihxUserID : userDetails?.userName || '',
      shortName: payer?.shortName || '',
    };
  } else if (admissionFormType === 'NORMAL') {
    data = {
      formType,
      patientName : admissionFormVal?.customerDetail?.fullName || '',
      patientPhoneNo : admissionFormVal?.customerDetail?.mobile || '',
      admissionType : '',
      hospitalName : hospitalDetails?.providerName || '',
      hospitalAddress : hospitalDetails?.providerAddress || '',
      hospitalNo : `${hospitalDetails?.providerId}` || '',
      ipNo : admissionFormVal?.customerDetail?.inPatientNumber || '',
      hospitalisation : '',
      memberId : admissionFormVal?.customerDetail?.memberId || '',
      policyHolder : admissionFormVal?.customerDetail?.fullName || '',
      ihxUserID : userDetails?.userName || '',
      staffName : '',
      location : '',
      shortName: payer?.shortName || '',
    };
  } else if (admissionFormType === 'MULTIPAYER') {
    data = {
      formType,
      patientName : admissionFormVal?.otherFormDetail?.fullName || '',
      patientPhoneNo : admissionFormVal?.otherFormDetail?.mobile || '',
      admissionType : '',
      hospitalName : hospitalDetails?.providerName || '',
      hospitalAddress : hospitalDetails?.providerAddress || '',
      hospitalNo : `${hospitalDetails?.providerId}` || '',
      ipNo : admissionFormVal?.otherFormDetail?.inPatientNumber || '',
      hospitalisation : '',
      memberId : admissionFormVal?.otherFormDetail?.memberId || '',
      policyHolder : admissionFormVal?.otherFormDetail?.fullName || '',
      ihxUserID : userDetails?.userName || '',
      staffName : '',
      location : '',
      shortName: payer?.shortName || '',
    };
  }

  return data;
};

export const prepareConsentRequest = (formData: any): any => {
  const [purpose = {}] = PURPOSE_OF_REQUEST?.filter((ele: any): boolean => ele?.value === formData?.requestPurpose)
    ?.map(({ value: code, label: text }: any): any => ({ code, text }));

  const appDetail = ApplicationDetail.instance;

  return {
    consent: {
      purpose,
      patient: {
        id: formData?.patientIdentifier,
        name: formData?.name,
      },
      hiu: {
        id: appDetail.getHipId(),
      },
      hiTypes: formData?.healthInfoType || [],
      permission: {
        dateRange: {
          from: moment(formData?.healthInfoFrom).format(),
          to: moment(formData?.healthInfoTo).format(),
        },
        dataEraseAt: moment(formData?.consentExpiry).format(),
      },
    },
  };
};

export const formatGrievanceDTO = (reqObj: any): any => {
  const finalReqObj = {};
  if (reqObj?.id) {
    finalReqObj['Id'] = reqObj?.id;
  }
  if (reqObj?.priority) {
    finalReqObj['priority'] = reqObj?.priorityd;
  }
  if (reqObj?.payerEmailId) {
    finalReqObj['payerEmailId'] = reqObj?.payerEmailId;
  }
  if (reqObj?.subject) {
    finalReqObj['subject'] = reqObj?.subject;
  }
  if (reqObj?.mailBody) {
    finalReqObj['description'] = reqObj?.mailBody;
  }
  if (reqObj?.payerEmailIdCC) {
    finalReqObj['cc_emails'] = reqObj?.payerEmailIdCC;
  }
  if (reqObj?.PayerId) {
    finalReqObj['PayerId'] = reqObj?.PayerId;
  }
  if (reqObj?.ticketId) {
    finalReqObj['TicketId'] = reqObj?.ticketId;
  }
  return finalReqObj;
};

export const rearrangeMetricsDashboardFormFilters = (data: any): any => {
  return [
    {
      name: 'searchCenter',
      value: data?.searchCenter,
      errors: [],
    },
    {
      name: 'searchPayer',
      value: data?.searchPayer,
      errors: [],
    },
    {
      name: 'fromMonth',
      value: data?.fromMonth,
      errors: [],
    },
    {
      name: 'toMonth',
      value: data?.toMonth,
      errors: [],
    },
  ];
};

export const getTotalFileUploadSize = (payer: any, nextStatus: any): number => {
  const totalSizeObj = JSON.parse(payer?.totalFileSizeInMB || '{}');
  try {
    if (totalSizeObj[nextStatus]) {
      return Number(totalSizeObj[nextStatus]);
    } else {
      return 0;
    }
  } catch (e) {
    return 0;
  }
};

export const getMinFileCount = (payer: any, nextStatus: any): number => {
  const minFileCount = JSON.parse(payer?.minFileCount || '{}');
  try {
    if (minFileCount[nextStatus]) {
      return Number(minFileCount[nextStatus]);
    } else {
      return 0;
    }
  } catch (e) {
    return 0;
  }
};

export const validateEmails = (EmailStr: string, savedEmails: any[]): any => {
  if (!EmailStr) {
    // If EmailStr is undefined or empty
    centeredNotification(MAIL_CONFIGURATION_CONSTANTS?.EMPTY_STRING);
    return '';
  }
  let isValidated = true;
  const emailArr = EmailStr?.split(',')?.map((email: any): any => email?.trim());
  const invalidEmails = emailArr?.filter((email: any): any => !EMAIL_REGX?.test(email));
  if (invalidEmails?.length > 0) {
    isValidated = false;
    centeredNotification(`"${invalidEmails}" should be in correct format`);
  } else {
    const duplicateEmails = [] as any;
    const upperCaseSavedEmails = savedEmails?.map((mail: any): any => mail?.toUpperCase());
    emailArr?.forEach((email: any, index: any): any => {
      if (upperCaseSavedEmails?.includes(email.toUpperCase())) {
        duplicateEmails?.push(email);
      }
    });
    if (duplicateEmails?.length > 0) {
      isValidated = false;
      centeredNotification(`"${String(duplicateEmails)}" is already configured, only unique emails ids are allowed`);
    } else {
      const duplicateEmailIds = [];
      const uniqueEmails = new Set();
      emailArr?.forEach((email: any): any =>  {
        if (uniqueEmails?.has(email)) {
          duplicateEmailIds?.push(email);
        }
        uniqueEmails?.add(email);
      });
      if (duplicateEmailIds?.length > 0) {
        isValidated = false;
        centeredNotification('Email ids should be unique');
      }
    }
  }
  return (isValidated ? String(emailArr) : '');
};

export const getDoctorMobileFormat = (mobile: string): string => {
  if (!mobile || typeof mobile !== 'string' || mobile.length !== 10) {
    return '';
  }
  // Remove '+91' and spaces if present, then format in required structure
  mobile = mobile.replace(/^\+91\s*/, '').replace(/\s+/g, '').trim();
  const formattedMobile = `+91 ${mobile.slice(0, 3)} ${mobile.slice(3, 7)} ${mobile.slice(7)}`;
  // Validate against regex to block all identical digits
  if (!MOBILENO_REPEAT_SEQUENCE_REGEX.test(formattedMobile)) {
    return ''; // Invalid number, do not prefill
  }
  return formattedMobile;
};

export const maskName = (name: string): string => {
  if (!name) {
    return '';
  }
  const [firstName, lastName] = name.split(' ');
  const maskedFirstName = firstName
    ? firstName.charAt(0) + '*'.repeat(firstName.length - 1)
    : '';
  const maskedLastName = lastName
    ? lastName.charAt(0) + '*'.repeat(lastName.length - 1)
    : '';
  return `${maskedFirstName} ${maskedLastName}`.trim();
};

export const processSearchInput = (
  input: string,
  options: {
    onlyAlphaNumeric?: boolean;
    onlyAlpha?: boolean;
    maxLengthValue?: boolean;
    fieldLengthValue?: number[];
    MAX_CHARACTERS_LIMIT: number;
  },
  callback?: (result: string) => void,
): string => {
  let validStr = convertToValidStr(input);

  if (options?.onlyAlphaNumeric) {
    validStr = validStr?.replace(ONLY_ALPHA_NUMERIC, '');
  }

  if (options?.onlyAlpha) {
    validStr = validStr?.replace(ONLY_ALPHABET, '');
  }

  const maxLength = options?.maxLengthValue
    ? options?.fieldLengthValue?.[0] || 0
    : options?.MAX_CHARACTERS_LIMIT;

  validStr = validStr?.slice(0, maxLength);

  if (callback) {
    callback(validStr);
  }
  return validStr;
};

export const defaultStartDate = format(startOfDay(subDays(new Date(), 30)), HIS_COMMON_DATE_FORMAT);
export const defaultEndDate = format(endOfDay(new Date()), HIS_COMMON_DATE_FORMAT);
export const defaultStartTime = format(startOfDay(subDays(new Date(), 30)), UPC_TIME_FORMAT);
export const defaultEndDateTime = format(endOfDay(new Date()), UPC_TIME_FORMAT);

export const DEFAULT_END_TIME_TPA = (): string => moment()?.format('hh:mm A');

export const isSuperAdmin = (): boolean => {
  const user = (ApplicationDetail.instance?.getUserRole() || [])?.find(
    ({ roleName }: any): boolean => roleName === 'SUPER_ADMIN',
  );
  return !!user;
};

export const isGroupUser = (): boolean => {
  const user = (ApplicationDetail.instance?.getUserRole() || []).find(
    ({ roleName }: any): boolean => {
      return GROUPED_ROLES?.includes(roleName);
    },
  );
  return !!user;
};

export const isNonIntegratedPayer = (isRpaPayer: boolean, isOnMailPayer: boolean): boolean => {
  return (isRpaPayer || isOnMailPayer);
};

export const validateMobileFormat = (mobile: string, isExtraRegex?: boolean): boolean => {
  if (!mobile || typeof mobile !== 'string' || mobile.length !== 10) {
    return false;
  }
  if (!mobile.startsWith('+91')) {
    mobile = `+91 ${mobile.substring(0, 3)} ${mobile.substring(3, 7)} ${mobile.substring(7)}`;
  }
  // For Heritage and other payers
  const regex = isExtraRegex ? MOBILENO_REPEAT_SEQUENCE_REGEX : MOBILE_MASK_REGEX;
  return regex.test(mobile);
};

export const getEmailFormat = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  email = email.trim();
  return EMAIL_REGX.test(email);
};

export const isValidRole = (role: string): boolean => {
  const user = (ApplicationDetail.instance?.getUserRole() || []).find(
    ({ roleName }: any): boolean => {
    return roleName?.includes(role);
    },
  );
  return !!user;
};
export const handleResize = (setIs150PercentZoom: any): any => {
  const zoomLevel = window.devicePixelRatio * 100;
  setIs150PercentZoom(zoomLevel === 225);
};

export const convertUtcToIst = (utcDateString: any): string => {
  const utcDate = parseISO(utcDateString);
  const formattedIstDate = format(utcDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX');
  return formattedIstDate;
};

export const combineDateAndTime = (date: any, time: any): any => {
  const formattedDate = format(new Date(date), 'yyyy-MM-dd');
  const parsedDateTime = parse(
    `${formattedDate} ${time}`,
    'yyyy-MM-dd hh:mm a',
    new Date(),
  );
  return parsedDateTime.toString();
};

export const filterDateFilterOnly = (filters: any[]): any[] => {
  return filters.filter((item: any): any => item.key === DATE_FILTER);
};

export const isDigitizationFeatureEnabled = (featureEnabled: boolean): boolean => {
  const isUserRestricted = !isGroupUser() && !isSuperAdmin();
  return (featureEnabled && isUserRestricted);
};

export const controlHorizontalScroll = (getScrollContainer: any, position: string): void => {
  customGTMEvent({
    event: 'Clicked Horizontal Scroll',
  });
  const scrollContainer = getScrollContainer();
  if (scrollContainer) {
    if (position === 'right') {
      scrollContainer.scrollLeft += 300; // Scroll to the left
    } else if (position === 'left') {
      scrollContainer.scrollLeft -= 300; // Scroll to the right
    }
  }
};

export const formattedMobileNumber = (value: string): string => {
  return value?.replace('+91', '')?.replace(/\s+/g, '');
};

export const getRandomStyle = (consentOptionStyle: any, showRandomOption: boolean): any => {
  if (showRandomOption) {
    const randomFontSize = PATIENT_SIGNATURE_CONSTANTS.FONTSIZEOPTIONS[Math.floor(Math.random() *
      PATIENT_SIGNATURE_CONSTANTS.FONTSIZEOPTIONS.length)];
    const randomFontFamily = PATIENT_SIGNATURE_CONSTANTS.FONTFAMILYOPTIONS[Math.floor(Math.random() *
      PATIENT_SIGNATURE_CONSTANTS.FONTFAMILYOPTIONS.length)];
    const randomFontWeight = PATIENT_SIGNATURE_CONSTANTS.FONTFAMILYWEIGHT[Math.floor(Math.random() *
      PATIENT_SIGNATURE_CONSTANTS.FONTFAMILYWEIGHT.length)];
    return {
      fontSize: randomFontSize,
      fontFamily: randomFontFamily,
      fontWeight: randomFontWeight,
    };
  } else {
    return {
      fontSize: consentOptionStyle?.fontSize,
      fontFamily: consentOptionStyle?.fontFamily,
      fontWeight: consentOptionStyle?.fontWeight,
    };
  }
};

export const formatDate = (dateString: string): string => {
    try {
      const parsedDate = parse(dateString, 'dd MMM yyyy, hh:mm a', new Date());
      return format(parsedDate, 'dd MMM yyyy, hh:mm a');
    } catch (error) {
      return getDateWithTime(new Date());
    }
  };

export const checkPrimaryBucketSuffix = (additionalText: string): boolean => {
    switch (additionalText) {
      case 'TODAY':
        return true;
      default:
        return false;
    }
};

// Converts milliseconds to days/hours/minutes/seconds
export const convertMsToTime = (ms: number): any => { // we are using this because date fns will return with years,months...
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
  };
};

export const validateFollowUpEmails = (
  newEmails: string[],
  existingEmails: string[] = [],
): boolean => {
  if (newEmails?.length === 0) { return true; } // no emails to check

  const lastRawEmail = newEmails[newEmails.length - 1];
  const lastEmail = lastRawEmail?.trim().toLowerCase();

  if (!EMAIL_REGX.test(lastEmail)) {
    centeredNotification(`${CONFIGURATION_VALIDATIONS.emailIdValidation} ${lastRawEmail}`);
    return false;
  }

  const existingSet = new Set(existingEmails?.map((e: string): string => e?.trim().toLowerCase()));

  if (existingSet?.has(lastEmail)) {
    centeredNotification(`${CONFIGURATION_VALIDATIONS.duplicateEmail} ${lastRawEmail}`);
    return false;
  }
  return true;
};

export const getValidEmails = (emails: string[]): string[] => {// returns valid email array
  const validEmailSet = new Set<string>();

  emails?.forEach((email: string): void => {
    const trimmedEmail = email?.trim().toLowerCase();
    if (trimmedEmail && EMAIL_REGX.test(trimmedEmail)) {
      validEmailSet.add(trimmedEmail);
    }
  });

  return Array.from(validEmailSet);
};

export const formatNumberWithLeadingZero = (number: number): string => {
  return number < 10 ? `0${number}` : `${number}`; // to format a number with a leading zero if it's a single digit
};

export const createPayerLookUpReqPayload = (
  payerIds: number | number[],
  productCodeValue: string[] | undefined,
  masterTypes: string[],
  appService?: any,
  masterIds: string[] = [],
): Record<string, any> => {
  const payerIdsArray = Array.isArray(payerIds) ? payerIds : [payerIds];
  let basePayload = {
    getByPayerId: true,
    getByMasterType: true,
    payerIds: Array.isArray(payerIds) ? payerIds : [payerIds],
    masterTypes,
    productCode: productCodeValue,
    isDicFormat: true,
    getByProductCode: !!productCodeValue,
    getByPayerMasterId: false,
    PayerMasterIds: masterIds,
  };
  if (!isEmpty(masterIds)) {
    basePayload = {
      ...basePayload,
      getByPayerMasterId: true,
    };
  }
  if (!appService) {
    return basePayload;
  }

  return {
    ...basePayload,
    getAutomationProcedure:
      appService.getIsNewAutomationEnabled() ||
      (process.env.REACT_APP_IS_ENABLED_AUTOMATION_SIMULATION === 'true' &&
        appService.getIsNewAutomationEnabled() &&
        payerIdsArray.includes(516571) &&
        Number(appService.getProviderId()) === Number(AUTOMATION_SIMULATION_HOSPITAL)),
  };
};
